import {
  Box,
  Container,
  Divider,
  Link,
  Paper,
  Stack,
  Typography,
} from '@mui/material';

import { SUPPORT_EMAIL as supportEmail } from '../../lib/supportContact';

export default function TermsOfServicePage() {
  return (
    <Container maxWidth="md" sx={{ py: { xs: 4, md: 8 } }}>
      <Paper elevation={2} sx={{ p: { xs: 3, md: 5 }, borderRadius: 2 }}>
        <Stack spacing={3}>
          <Box>
            <Typography variant="h3" component="h1" gutterBottom>
              Terms of Service
            </Typography>
            <Typography color="text.secondary">
              Last updated: September 9, 2026
            </Typography>
            <Typography color="text.secondary" sx={{ mt: 2 }}>
              These Terms of Service govern your use of Makeen Real Estate
              Management ("Makeen", "the app", "we", "us", or "our").
            </Typography>
          </Box>

          <Divider />

          <Box>
            <Typography variant="h5" component="h2" gutterBottom>
              Acceptance of terms
            </Typography>
            <Typography color="text.secondary" sx={{ lineHeight: 1.8 }}>
              By creating an account, downloading, installing, accessing, or
              using Makeen, you agree to these Terms. If you do not agree, do
              not use the app or service.
            </Typography>
          </Box>

          <Box>
            <Typography variant="h5" component="h2" gutterBottom>
              Service description
            </Typography>
            <Typography color="text.secondary" sx={{ lineHeight: 1.8 }}>
              Makeen provides real estate management tools, including property,
              tenant, owner, buyer, manager, accountant, maintenance, contract,
              invoice, voucher, document, reporting, and notification workflows.
            </Typography>
          </Box>

          <Box>
            <Typography variant="h5" component="h2" gutterBottom>
              Accounts and responsibilities
            </Typography>
            <Typography component="ul" sx={{ pl: 3, lineHeight: 1.8 }}>
              <li>You must provide accurate and complete account information.</li>
              <li>You are responsible for keeping your credentials secure.</li>
              <li>You are responsible for activity under your account.</li>
              <li>You must notify us promptly of unauthorized access or suspected misuse.</li>
              <li>Privileged roles such as manager, accountant, and administrator must be assigned through authorized administrative processes.</li>
            </Typography>
          </Box>

          <Box>
            <Typography variant="h5" component="h2" gutterBottom>
              Acceptable use
            </Typography>
            <Typography component="ul" sx={{ pl: 3, lineHeight: 1.8 }}>
              <li>Use the app only for lawful real estate management purposes.</li>
              <li>Do not submit unlawful, harmful, misleading, or infringing content.</li>
              <li>Do not attempt to access accounts, roles, records, or systems without authorization.</li>
              <li>Do not interfere with service operation, security, or availability.</li>
              <li>Do not use the app to violate applicable laws, contracts, or third-party rights.</li>
              <li>Do not upload illegal, sexually explicit, hateful, threatening, harassing, abusive or privacy-infringing content.</li>
              <li>Report inappropriate content or users from Profile &gt; Report content or a user. Administrators investigate reports, remove violating content and restrict abusive accounts.</li>
            </Typography>
          </Box>

          <Box>
            <Typography variant="h5" component="h2" gutterBottom>
              Data and privacy
            </Typography>
            <Typography color="text.secondary" sx={{ lineHeight: 1.8 }}>
              Our <Link href="/privacy">Privacy Policy</Link> explains how we
              collect, use, store, retain, share, and delete information. You
              may request account deletion in the app from Profile &gt; Delete
              Account or from our <Link href="/account-deletion">account deletion page</Link>.
              Some contract, invoice, payment, tax, accounting, security, and
              dispute records may be retained when required by law or legitimate
              business obligations.
            </Typography>
          </Box>

          <Box>
            <Typography variant="h5" component="h2" gutterBottom>
              Financial and business records
            </Typography>
            <Typography color="text.secondary" sx={{ lineHeight: 1.8 }}>
              Makeen helps manage financial and business information, but it
              does not provide legal, tax, accounting, or financial advice. You
              are responsible for the accuracy of information entered into the
              app, proper record keeping, VAT and tax compliance, and decisions
              made using app reports.
            </Typography>
          </Box>

          <Box>
            <Typography variant="h5" component="h2" gutterBottom>
              Intellectual property
            </Typography>
            <Typography color="text.secondary" sx={{ lineHeight: 1.8 }}>
              The app, interface, code, content, and related materials are owned
              by Makeen or its licensors and are protected by applicable
              intellectual property laws. You may not copy, reverse engineer, or
              distribute the app except as allowed by law or written permission.
            </Typography>
          </Box>

          <Box>
            <Typography variant="h5" component="h2" gutterBottom>
              Service availability and changes
            </Typography>
            <Typography color="text.secondary" sx={{ lineHeight: 1.8 }}>
              We aim to provide a reliable service, but we do not guarantee
              uninterrupted or error-free operation. We may update, suspend, or
              modify features for maintenance, security, legal, or business
              reasons.
            </Typography>
          </Box>

          <Box>
            <Typography variant="h5" component="h2" gutterBottom>
              Termination
            </Typography>
            <Typography color="text.secondary" sx={{ lineHeight: 1.8 }}>
              We may suspend or terminate access when we believe an account has
              violated these Terms, created risk for other users, or threatened
              the security or operation of the service.
            </Typography>
          </Box>

          <Box>
            <Typography variant="h5" component="h2" gutterBottom>
              Governing law
            </Typography>
            <Typography color="text.secondary" sx={{ lineHeight: 1.8 }}>
              These Terms are governed by the laws of the Kingdom of Saudi Arabia,
              without regard to conflict of law principles.
            </Typography>
          </Box>

          <Box>
            <Typography variant="h5" component="h2" gutterBottom>
              Contact
            </Typography>
            <Typography color="text.secondary" sx={{ lineHeight: 1.8 }}>
              If you have questions about these Terms, use Contact Support in
              the app{supportEmail ? <> or email <Link href={`mailto:${supportEmail}`}>{supportEmail}</Link></> : null}.
            </Typography>
          </Box>

          <Typography variant="body2" color="text.secondary">
            See also <Link href="/privacy">Privacy Policy</Link> and{' '}
            <Link href="/account-deletion">Account Deletion</Link>.
          </Typography>
        </Stack>
      </Paper>
    </Container>
  );
}
